import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Card } from '@/Components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/Components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/Components/ui/popover';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { Separator } from '@/Components/ui/separator';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/Components/ui/sheet';
import { Switch } from '@/Components/ui/switch';
import { useToast } from '@/Hooks/use-toast';
import { cn } from '@/lib/utils';
import {
    ExternalLink,
    Info,
    Loader2,
    LogOut,
    MessageCircle,
    Plus,
    Send,
    ShieldAlert,
    User,
    UserPlus,
    X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';

interface ChatMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
    timestamp?: string;
}

const Chatbot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isGdprDialogOpen, setIsGdprDialogOpen] = useState(false);
    const [isPrivacySheetOpen, setIsPrivacySheetOpen] = useState(false);
    const [isUserRegistrationOpen, setIsUserRegistrationOpen] = useState(false);
    const [isAiUnavailable, setIsAiUnavailable] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('+1234567890');
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        isRegistered: false,
    });
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasGdprConsent, setHasGdprConsent] = useState<boolean | null>(null);
    const [showPrivacyBadge, setShowPrivacyBadge] = useState(false);
    const [privacySettings, setPrivacySettings] = useState({
        storeConversations: true,
        useThirdPartyAI: true,
        allowPersonalization: true,
        receiveUpdates: false,
    });

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    useEffect(() => {
        // Check GDPR consent status
        const gdprConsent = localStorage.getItem('gdprChatConsent');
        setHasGdprConsent(gdprConsent === 'true');

        // Show privacy badge if no GDPR consent yet
        if (!gdprConsent) {
            setShowPrivacyBadge(true);
        }

        // Load user's privacy settings if available
        const storedSettings = localStorage.getItem('chatPrivacySettings');
        if (storedSettings) {
            try {
                setPrivacySettings(JSON.parse(storedSettings));
            } catch (e) {
                console.error('Failed to parse stored privacy settings');
            }
        }

        // Load stored user info
        const storedUserInfo = localStorage.getItem('chatUserInfo');
        if (storedUserInfo) {
            try {
                const parsedUserInfo = JSON.parse(storedUserInfo);
                setUserInfo(parsedUserInfo);
            } catch (e) {
                console.error('Failed to parse stored user info');
            }
        }

        // Check AI status on component mount
        checkAiStatus(true);
    }, []);

    const checkAiStatus = async (forceSetWelcome = false) => {
        try {
            setIsCheckingStatus(true);
            const response = await fetch('/chat/status', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setIsAiUnavailable(!data.ai_available);
                setWhatsappNumber(data.whatsapp_number);

                // Only set initial welcome message if forced or no messages exist
                if (forceSetWelcome || messages.length === 0) {
                    const welcomeMessage: ChatMessage = {
                        role: 'system',
                        content: data.ai_available
                            ? "Welcome to Gaphen Projects! I'm your AI assistant. How can I help you today?"
                            : 'Welcome to Gaphen Projects! Our AI assistant is currently unavailable, but you can contact us directly on WhatsApp for immediate assistance.',
                        timestamp: new Date().toISOString(),
                    };

                    setMessages([welcomeMessage]);
                }
            }
        } catch (error) {
            console.error('Error checking AI status:', error);
            setIsAiUnavailable(true);

            // Only set fallback message if forced or no messages exist
            if (forceSetWelcome || messages.length === 0) {
                const fallbackMessage: ChatMessage = {
                    role: 'system',
                    content:
                        'Welcome to Gaphen Projects! For assistance, please contact us on WhatsApp.',
                    timestamp: new Date().toISOString(),
                };

                setMessages([fallbackMessage]);
            }
        } finally {
            setIsCheckingStatus(false);
        }
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        // Focus input when chat is opened
        if (isOpen && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleSendMessage = async () => {
        if (!input.trim() || isLoading) return;

        // Check if GDPR consent is given
        if (hasGdprConsent === false) {
            setIsGdprDialogOpen(true);
            return;
        }

        if (hasGdprConsent === null) {
            // If consent status is unknown, assume not given and show dialog
            setIsGdprDialogOpen(true);
            return;
        }

        // If AI is available and user is not registered, show registration dialog
        if (!isAiUnavailable && !userInfo.isRegistered) {
            setIsUserRegistrationOpen(true);
            return;
        }

        // Send the message
        await sendMessageToServer();
    };

    const handleGiveConsent = async () => {
        // Save GDPR consent
        localStorage.setItem('gdprChatConsent', 'true');
        setHasGdprConsent(true);
        setIsGdprDialogOpen(false);
        setShowPrivacyBadge(false);

        // Save privacy settings
        localStorage.setItem(
            'chatPrivacySettings',
            JSON.stringify(privacySettings),
        );

        // Send the message now that consent is given
        await handleSendMessage();

        // Send consent to server if user is authenticated
        try {
            await fetch('/gdpr/cookie-consent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN':
                        document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute('content') || '',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    preferences: {
                        necessary: true,
                        functional: true,
                        analytics: privacySettings.storeConversations,
                        marketing: privacySettings.receiveUpdates,
                    },
                    version: '1.0',
                }),
            });
        } catch (error) {
            console.error('Error saving consent:', error);
        }
    };

    const handleUpdatePrivacySettings = () => {
        // Save updated privacy settings
        localStorage.setItem(
            'chatPrivacySettings',
            JSON.stringify(privacySettings),
        );
        setIsPrivacySheetOpen(false);

        toast({
            title: 'Privacy settings updated',
            description: 'Your privacy preferences have been saved.',
            duration: 3000,
        });
    };
    const handleUserRegistration = async (name: string, email: string) => {
        const newUserInfo = {
            name,
            email,
            isRegistered: true,
        };

        setUserInfo(newUserInfo);
        localStorage.setItem('chatUserInfo', JSON.stringify(newUserInfo));
        setIsUserRegistrationOpen(false);

        // Now send the original message that was typed
        await sendMessageToServer();
    };

    const handleSkipUserRegistration = async () => {
        // Generate unique default details using session ID
        const sessionId =
            localStorage.getItem('chatSessionId') ||
            `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('chatSessionId', sessionId);

        const defaultUserInfo = {
            name: `User_${sessionId.slice(-8)}`, // Use last 8 characters of session ID
            email: `${sessionId}@gaphen.com`,
            isRegistered: true,
        };

        setUserInfo(defaultUserInfo);
        localStorage.setItem('chatUserInfo', JSON.stringify(defaultUserInfo));
        setIsUserRegistrationOpen(false);

        // Now send the original message that was typed
        await sendMessageToServer();
    };

    const sendMessageToServer = async () => {
        if (!input.trim() || isLoading) return;

        // Add user message
        const userMessage: ChatMessage = {
            role: 'user',
            content: input,
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage]);
        const currentInput = input; // Store the input before clearing
        setInput('');
        setIsLoading(true);

        try {
            // Send message to server
            const response = await fetch('/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN':
                        document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    message: currentInput,
                    name: userInfo.name || 'Anonymous User',
                    email: userInfo.email || 'user@example.com',
                    sessionId:
                        localStorage.getItem('chatSessionId') ||
                        `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    type: 'question',
                    privacy_consent: true,
                    data_processing_consent: true,
                    third_party_sharing_consent: true,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to send message');
            }

            const data = await response.json();

            // Check if AI is unavailable
            if (data.ai_unavailable) {
                setIsAiUnavailable(true);

                // Update WhatsApp number if provided
                if (data.whatsapp_number) {
                    setWhatsappNumber(data.whatsapp_number);
                }

                // Add WhatsApp fallback message
                const fallbackMessage: ChatMessage = {
                    role: 'assistant',
                    content:
                        data.message ||
                        "I'm temporarily unable to process your request through AI. For immediate assistance, please contact us on WhatsApp.",
                    timestamp: new Date().toISOString(),
                };

                setMessages((prev) => [...prev, fallbackMessage]);
                return;
            }

            // Store session ID if not already stored
            const sessionId =
                localStorage.getItem('chatSessionId') ||
                `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('chatSessionId', sessionId);

            // Add assistant response
            const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: data.message || data.response,
                timestamp: new Date().toISOString(),
            };

            setMessages((prev) => [...prev, assistantMessage]);
            setIsAiUnavailable(false);
        } catch (error) {
            console.error('Error sending message:', error);
            setIsAiUnavailable(true);

            // Add error message with WhatsApp fallback
            const errorMessage: ChatMessage = {
                role: 'system',
                content:
                    'Sorry, I encountered an error. For immediate assistance, please contact us on WhatsApp.',
                timestamp: new Date().toISOString(),
            };

            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const toggleChatbot = () => {
        setIsOpen(!isOpen);
    };

    const openWhatsApp = () => {
        const message = encodeURIComponent(
            'Hello! I need assistance from Gaphen Projects.',
        );
        const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const renderMessage = (message: ChatMessage, index: number) => {
        const isUser = message.role === 'user';
        const isSystem = message.role === 'system';

        return (
            <div
                key={index}
                className={cn(
                    'group flex w-full items-start gap-2 px-4 py-2',
                    isUser ? 'justify-end' : 'justify-start',
                    isSystem && 'justify-center text-center',
                )}
            >
                {!isUser && !isSystem && (
                    <div className="flex h-8 w-8 shrink-0 select-none overflow-hidden rounded-md border bg-background shadow">
                        <img
                            src="/images/gaphen-ai-avatar.png"
                            alt="Gaphen AI"
                            className="h-full w-full object-cover"
                        />
                    </div>
                )}

                <div
                    className={cn(
                        'flex max-w-[80%] flex-col gap-2 rounded-lg px-3 py-2 text-sm',
                        isUser
                            ? 'bg-primary text-primary-foreground'
                            : isSystem
                              ? 'bg-muted py-1 text-xs text-muted-foreground'
                              : 'bg-muted',
                    )}
                >
                    {message.content.split('\n').map((text, i) => (
                        <div key={i} className={cn(isSystem ? 'italic' : '')}>
                            <Markdown>{text}</Markdown>
                        </div>
                    ))}

                    {message.timestamp && (
                        <div
                            className={cn(
                                'ml-auto text-[10px]',
                                isUser
                                    ? 'text-primary-foreground/50'
                                    : 'text-muted-foreground',
                                isSystem && 'hidden',
                            )}
                        >
                            {formatTimestamp(message.timestamp)}
                        </div>
                    )}
                </div>

                {isUser && (
                    <div className="flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-md border bg-background shadow">
                        <User className="h-4 w-4" />
                    </div>
                )}
            </div>
        );
    };

    // Load messages from localStorage on mount and user change
    useEffect(() => {
        if (userInfo.isRegistered) {
            const sessionId =
                localStorage.getItem('chatSessionId') ||
                `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('chatSessionId', sessionId);

            const messagesKey = `chatMessages_${userInfo.email}`;
            const storedMessages = localStorage.getItem(messagesKey);

            if (storedMessages && privacySettings.storeConversations) {
                try {
                    const parsedMessages = JSON.parse(storedMessages);
                    setMessages(parsedMessages);
                } catch (e) {
                    console.error('Failed to parse stored messages');
                    // Set default welcome message if parsing fails
                    checkAiStatus(true);
                }
            } else if (messages.length === 0) {
                // Load initial welcome message if no stored messages
                checkAiStatus(true);
            }
        }
    }, [
        userInfo.isRegistered,
        userInfo.email,
        privacySettings.storeConversations,
    ]);

    // Save messages to localStorage whenever messages change
    useEffect(() => {
        if (
            userInfo.isRegistered &&
            messages.length > 0 &&
            privacySettings.storeConversations
        ) {
            const messagesKey = `chatMessages_${userInfo.email}`;
            localStorage.setItem(messagesKey, JSON.stringify(messages));
        }
    }, [
        messages,
        userInfo.isRegistered,
        userInfo.email,
        privacySettings.storeConversations,
    ]);

    const startNewChat = () => {
        setMessages([]);
        setInput('');

        // Clear stored messages for current user
        if (userInfo.isRegistered) {
            const messagesKey = `chatMessages_${userInfo.email}`;
            localStorage.removeItem(messagesKey);
        }

        // Load fresh welcome message
        checkAiStatus(true);

        toast({
            title: 'New chat started',
            description: 'Previous conversation has been cleared.',
            duration: 3000,
        });
    };

    const handleLogout = () => {
        // Clear user info
        setUserInfo({
            name: '',
            email: '',
            isRegistered: false,
        });

        // Clear stored user info
        localStorage.removeItem('chatUserInfo');

        // Clear current messages
        setMessages([]);
        setInput('');

        // Close chat
        setIsOpen(false);

        toast({
            title: 'Logged out',
            description: 'You have been logged out successfully.',
            duration: 3000,
        });
    };

    const handleSwitchUser = () => {
        handleLogout();
        setIsUserRegistrationOpen(true);
    };

    return (
        <>
            {/* Chatbot Button */}
            <div className="fixed bottom-6 right-6 z-50">
                <div className="relative">
                    {/* Privacy Badge */}
                    {showPrivacyBadge && (
                        <Badge
                            variant="destructive"
                            className="absolute -right-2 -top-2 z-10 animate-pulse"
                        >
                            <ShieldAlert className="mr-1 h-3 w-3" />
                            <span className="text-xs">Privacy</span>
                        </Badge>
                    )}

                    {/* AI Status Badge - Only show WhatsApp option when AI is unavailable */}
                    {isAiUnavailable && (
                        <Badge
                            variant="outline"
                            className="absolute -left-2 -top-2 z-10 border-green-200 bg-green-100 text-green-800"
                        >
                            <MessageCircle className="mr-1 h-3 w-3" />
                            <span className="text-xs">WhatsApp</span>
                        </Badge>
                    )}

                    <Button
                        onClick={toggleChatbot}
                        size="icon"
                        className={cn(
                            'h-14 w-14 rounded-full shadow-lg transition-all duration-200',
                            isAiUnavailable
                                ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800'
                                : 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-800',
                        )}
                        aria-label={isOpen ? 'Close chat' : 'Open chat'}
                    >
                        {isOpen ? (
                            <X className="h-6 w-6" />
                        ) : (
                            <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full">
                                <img
                                    src="/images/gaphen-ai-avatar.png"
                                    alt="Gaphen AI"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        )}
                    </Button>
                </div>
            </div>

            {/* Chatbot Dialog */}
            {isOpen && (
                <Card
                    className={cn(
                        'fixed bottom-24 right-6 z-50 flex w-full flex-col rounded-lg border-2 shadow-xl',
                        'h-[600px] max-h-[calc(100vh-120px)] bg-card text-card-foreground',
                        'transition-all duration-300 ease-in-out',
                        'sm:w-96 md:w-[420px]',
                        isOpen
                            ? 'scale-100 opacity-100'
                            : 'pointer-events-none scale-95 opacity-0',
                    )}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b p-4">
                        <div className="flex items-center gap-3">
                            <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-900">
                                <img
                                    src="/images/gaphen-ai-avatar.png"
                                    alt="Gaphen AI"
                                    className="h-full w-full object-cover"
                                />
                                {/* Status Indicator - Only show if checking */}
                                {isCheckingStatus && (
                                    <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-background bg-yellow-500">
                                        <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-300" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-foreground">
                                    Gaphen Assistant
                                </h3>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-muted-foreground">
                                        {isCheckingStatus
                                            ? 'Connecting...'
                                            : isAiUnavailable
                                              ? 'Contact us on WhatsApp for assistance'
                                              : 'How can I help you today?'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {/* New Chat Button */}
                            {!isAiUnavailable &&
                                userInfo.isRegistered &&
                                messages.length > 1 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={startNewChat}
                                        className="gap-2"
                                        title="Start new conversation"
                                    >
                                        <Plus className="h-4 w-4" />
                                        New Chat
                                    </Button>
                                )}

                            {/* User Menu */}
                            {userInfo.isRegistered && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <User className="h-4 w-4" />
                                            <span className="sr-only">
                                                User menu
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-56"
                                    >
                                        <DropdownMenuLabel>
                                            <div className="flex flex-col space-y-1">
                                                <p className="text-sm font-medium leading-none">
                                                    {userInfo.name}
                                                </p>
                                                <p className="text-xs leading-none text-muted-foreground">
                                                    {userInfo.email}
                                                </p>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={startNewChat}
                                        >
                                            <Plus className="mr-2 h-4 w-4" />
                                            <span>New Chat</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={handleSwitchUser}
                                        >
                                            <UserPlus className="mr-2 h-4 w-4" />
                                            <span>Switch User</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={handleLogout}
                                        >
                                            <LogOut className="mr-2 h-4 w-4" />
                                            <span>Logout</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}

                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                    >
                                        <Info className="h-4 w-4" />
                                        <span className="sr-only">
                                            Information
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80" align="end">
                                    <div className="space-y-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-10 w-10 overflow-hidden rounded-full border">
                                                <img
                                                    src="/images/gaphen-ai-avatar.png"
                                                    alt="Gaphen AI"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <h4 className="font-medium">
                                                About this assistant
                                            </h4>
                                        </div>
                                        <p className="text-muted-foreground">
                                            I can help you with information
                                            about Gaphen Projects, our services,
                                            solutions, and general questions.
                                            I'm an AI assistant trained to
                                            provide helpful and accurate
                                            information.
                                        </p>
                                        <p className="text-muted-foreground">
                                            Your conversations may be stored to
                                            improve our service, subject to your
                                            privacy preferences.
                                        </p>
                                        {isAiUnavailable && (
                                            <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-900/20">
                                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                                    AI services are temporarily
                                                    unavailable. Please use
                                                    WhatsApp for immediate
                                                    assistance.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>

                            <Sheet
                                open={isPrivacySheetOpen}
                                onOpenChange={setIsPrivacySheetOpen}
                            >
                                <SheetTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                    >
                                        <ShieldAlert className="h-4 w-4" />
                                        <span className="sr-only">
                                            Privacy Settings
                                        </span>
                                    </Button>
                                </SheetTrigger>
                                <SheetContent className="sm:max-w-md">
                                    <SheetHeader>
                                        <SheetTitle>
                                            Privacy Settings
                                        </SheetTitle>
                                        <SheetDescription>
                                            Manage your chatbot privacy
                                            preferences. These settings control
                                            how your data is used.
                                        </SheetDescription>
                                    </SheetHeader>
                                    <div className="space-y-5 py-6">
                                        <div className="flex flex-col space-y-3">
                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="store-chats"
                                                    className="flex flex-col space-y-1"
                                                >
                                                    <span>
                                                        Store conversations
                                                    </span>
                                                    <span className="text-xs font-normal text-muted-foreground">
                                                        Allow us to store your
                                                        conversations for
                                                        service improvement
                                                    </span>
                                                </Label>
                                                <Switch
                                                    id="store-chats"
                                                    checked={
                                                        privacySettings.storeConversations
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        setPrivacySettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                storeConversations:
                                                                    checked,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </div>

                                            <Separator />

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="third-party"
                                                    className="flex flex-col space-y-1"
                                                >
                                                    <span>
                                                        Use third-party AI
                                                        services
                                                    </span>
                                                    <span className="text-xs font-normal text-muted-foreground">
                                                        Allow use of external AI
                                                        services to process your
                                                        queries
                                                    </span>
                                                </Label>
                                                <Switch
                                                    id="third-party"
                                                    checked={
                                                        privacySettings.useThirdPartyAI
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        setPrivacySettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                useThirdPartyAI:
                                                                    checked,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </div>

                                            <Separator />

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="personalization"
                                                    className="flex flex-col space-y-1"
                                                >
                                                    <span>
                                                        Allow personalization
                                                    </span>
                                                    <span className="text-xs font-normal text-muted-foreground">
                                                        Enable personalized
                                                        responses based on your
                                                        chat history
                                                    </span>
                                                </Label>
                                                <Switch
                                                    id="personalization"
                                                    checked={
                                                        privacySettings.allowPersonalization
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        setPrivacySettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                allowPersonalization:
                                                                    checked,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </div>

                                            <Separator />

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="updates"
                                                    className="flex flex-col space-y-1"
                                                >
                                                    <span>Receive updates</span>
                                                    <span className="text-xs font-normal text-muted-foreground">
                                                        Receive updates about
                                                        new features and
                                                        services
                                                    </span>
                                                </Label>
                                                <Switch
                                                    id="updates"
                                                    checked={
                                                        privacySettings.receiveUpdates
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        setPrivacySettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                receiveUpdates:
                                                                    checked,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end space-x-2 pt-4">
                                        <Button
                                            variant="outline"
                                            onClick={() =>
                                                setIsPrivacySheetOpen(false)
                                            }
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={
                                                handleUpdatePrivacySettings
                                            }
                                        >
                                            Save Changes
                                        </Button>
                                    </div>
                                </SheetContent>
                            </Sheet>

                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={toggleChatbot}
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </Button>
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <ScrollArea className="flex-1 p-4">
                        <div className="flex flex-col gap-4">
                            {messages.map(renderMessage)}
                            {isLoading && (
                                <div className="flex w-full items-center justify-start gap-2 px-4">
                                    <div className="relative flex h-8 w-8 shrink-0 select-none overflow-hidden rounded-md border bg-background shadow">
                                        <img
                                            src="/images/gaphen-ai-avatar.png"
                                            alt="Gaphen AI"
                                            className="h-full w-full object-cover"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                                            <Loader2 className="h-4 w-4 animate-spin text-white" />
                                        </div>
                                    </div>
                                    <div className="rounded-lg bg-muted px-3 py-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="animate-pulse">
                                                Thinking
                                            </span>
                                            <span className="flex">
                                                <span className="mx-[1px] mt-2 h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]"></span>
                                                <span className="mx-[1px] mt-2 h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]"></span>
                                                <span className="mx-[1px] mt-2 h-1.5 w-1.5 animate-bounce rounded-full bg-current"></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </ScrollArea>

                    {/* Input Area */}
                    <div className="border-t p-4">
                        {isAiUnavailable && (
                            <div className="mb-3 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                                <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                                    <MessageCircle className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                        Get Instant Help
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                                    Our team is ready to assist you on WhatsApp
                                    for immediate support.
                                </p>
                                <Button
                                    onClick={openWhatsApp}
                                    className="mt-2 w-full gap-2 bg-green-600 text-white hover:bg-green-700"
                                    size="sm"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Chat on WhatsApp
                                    <ExternalLink className="h-3 w-3" />
                                </Button>
                            </div>
                        )}

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSendMessage();
                            }}
                            className="flex items-center gap-2"
                        >
                            <Input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={
                                    isAiUnavailable
                                        ? 'Use WhatsApp for instant assistance'
                                        : 'Type your message...'
                                }
                                className="flex-1"
                                disabled={isLoading || isAiUnavailable}
                            />
                            <Button
                                type="submit"
                                size="icon"
                                disabled={
                                    isLoading ||
                                    !input.trim() ||
                                    isAiUnavailable
                                }
                                className={cn(
                                    isAiUnavailable &&
                                        'cursor-not-allowed opacity-50',
                                )}
                            >
                                <Send className="h-4 w-4" />
                                <span className="sr-only">Send</span>
                            </Button>
                        </form>
                    </div>
                </Card>
            )}

            {/* User Registration Dialog */}
            <Dialog
                open={isUserRegistrationOpen}
                onOpenChange={setIsUserRegistrationOpen}
            >
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <div className="mb-4 flex items-center justify-center">
                            <div className="flex h-16 w-16 overflow-hidden rounded-full border-2 border-emerald-200 shadow-sm dark:border-emerald-800">
                                <img
                                    src="/images/gaphen-ai-avatar.png"
                                    alt="Gaphen AI"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <DialogTitle className="text-center">
                            Let's Get Started!
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            To provide you with personalized assistance, please
                            share your details.
                        </DialogDescription>
                    </DialogHeader>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            const name = formData.get('name') as string;
                            const email = formData.get('email') as string;

                            if (name.trim() && email.trim()) {
                                handleUserRegistration(
                                    name.trim(),
                                    email.trim(),
                                );
                            }
                        }}
                        className="space-y-4 py-4"
                    >
                        <div className="space-y-2">
                            <Label htmlFor="user-name">Name</Label>
                            <Input
                                id="user-name"
                                name="name"
                                type="text"
                                placeholder="Enter your name"
                                required
                                className="w-full"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="user-email">Email</Label>
                            <Input
                                id="user-email"
                                name="email"
                                type="email"
                                placeholder="Enter your email"
                                required
                                className="w-full"
                            />
                        </div>

                        <div className="rounded-md bg-muted p-3">
                            <p className="text-sm text-muted-foreground">
                                Your information helps us provide better
                                assistance and allows us to follow up if needed.
                            </p>
                        </div>

                        <DialogFooter className="flex-col gap-2 sm:flex-row">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleSkipUserRegistration}
                            >
                                Skip for now
                            </Button>
                            <Button type="submit">Start Chatting</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* GDPR Consent Dialog */}
            <Dialog open={isGdprDialogOpen} onOpenChange={setIsGdprDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <div className="mb-4 flex items-center justify-center">
                            <div className="flex h-16 w-16 overflow-hidden rounded-full border-2 border-emerald-200 shadow-sm dark:border-emerald-800">
                                <img
                                    src="/images/gaphen-ai-avatar.png"
                                    alt="Gaphen AI"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <DialogTitle className="flex items-center justify-center">
                            <ShieldAlert className="mr-2 h-5 w-5 text-amber-500" />
                            Privacy Consent Required
                        </DialogTitle>
                        <DialogDescription>
                            Before using the chat assistant, we need your
                            consent to process your data according to our
                            privacy policy.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium">
                                By using this chatbot, you consent to:
                            </h4>
                            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                                <li>
                                    Your messages being stored and processed to
                                    provide you with assistance
                                    {privacySettings.storeConversations
                                        ? ''
                                        : ' (currently disabled in your privacy settings)'}
                                </li>
                                <li>
                                    The use of third-party AI services to
                                    process your queries
                                    {privacySettings.useThirdPartyAI
                                        ? ''
                                        : ' (currently disabled in your privacy settings)'}
                                </li>
                                <li>Our privacy policy and terms of service</li>
                            </ul>
                        </div>

                        <div className="rounded-md bg-muted p-3">
                            <p className="text-sm">
                                You can adjust your privacy settings at any time
                                using the privacy settings button (shield icon)
                                in the chat window.
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button
                            variant="outline"
                            onClick={() => setIsGdprDialogOpen(false)}
                        >
                            Decline
                        </Button>
                        <Button onClick={handleGiveConsent}>
                            Accept & Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Chatbot;
